import apiClient from './Client';

// ASSUMED CONTRACT — the printpros backend was not available when this was written.
// Every backend field name the banner editor relies on lives in this file; correct them here.
//   GET /Banner/get-banner/{id}      → RawBanner (width/height in inches)
//   GET /Product/GetProductTypeList  → RawProductType[] (bleed/safetyMargin in inches)
//   PUT /Banner/update/{id}          ← BannerUpdateBody

interface RawBanner {
  bannerID: number;
  bannerName: string;
  productTypeId: number;
  width: number;
  height: number;
  jsonObject: string | null;
  dateCreated: string;
  lastUpdated: string | null;
}

interface RawProductType {
  productTypeID: number;
  productTypeName: string;
  bleed: number;
  safetyMargin: number;
}

interface BannerUpdateBody {
  jsonObject: string;
  uploadIds: number[];
}

/** Uploaded-photo bucket the banner editor lists and filters (FilesStorage/retrieve-uploaded-photos/{type}). */
export const BANNER_PHOTO_TYPE = 'banner';

export interface BannerDetails {
  id: number;
  name: string;
  widthIn: number;
  heightIn: number;
  bleedIn: number;
  safetyIn: number;
  designJson: any | null;
  dateCreated: string;
  lastUpdated: string | null;
}

// Mock mode (REACT_APP_USE_MOCK_BANNER=true, e.g. in .env.local): opens the editor without the Banner API.
// Every id gets a 24" x 18" yard sign; saves go to localStorage so reopening restores them.
const USE_MOCK = process.env.REACT_APP_USE_MOCK_BANNER === 'true';
const mockKey = (bannerId: number) => `mockBanner:${bannerId}`;

function getMockBanner(bannerId: number): BannerDetails {
  const saved = localStorage.getItem(mockKey(bannerId));
  const { jsonObject, lastUpdated } = saved ? JSON.parse(saved) : { jsonObject: null, lastUpdated: null };
  return {
    id: bannerId,
    name: 'Yard Sign',
    widthIn: 24,
    heightIn: 18,
    bleedIn: 0.125,
    safetyIn: 0.25,
    designJson: jsonObject ? JSON.parse(jsonObject) : null,
    dateCreated: '2026-05-22T00:00:00Z',
    lastUpdated,
  };
}

class BannerService {
  /** Loads the banner and its product type's bleed and safety margin. */
  async GetBannerDetails(bannerId: number): Promise<BannerDetails> {
    if (USE_MOCK) return getMockBanner(bannerId);

    const [bannerRes, typesRes] = await Promise.all([
      apiClient.get<RawBanner>(`/Banner/get-banner/${bannerId}`),
      apiClient.get<RawProductType[]>('/Product/GetProductTypeList'),
    ]);
    const banner = bannerRes.data;
    const type = (typesRes.data || []).find(t => t.productTypeID === banner.productTypeId);
    if (!type) throw new Error(`Product type ${banner.productTypeId} not found for banner ${bannerId}`);

    return {
      id: banner.bannerID,
      name: banner.bannerName,
      widthIn: banner.width,
      heightIn: banner.height,
      bleedIn: type.bleed ?? 0,
      safetyIn: type.safetyMargin ?? 0,
      designJson: banner.jsonObject ? JSON.parse(banner.jsonObject) : null,
      dateCreated: banner.dateCreated,
      lastUpdated: banner.lastUpdated,
    };
  }

  async UpdateBanner(bannerId: number, designJson: any, uploadIds: number[]) {
    const body: BannerUpdateBody = { jsonObject: JSON.stringify(designJson), uploadIds };
    if (USE_MOCK) {
      localStorage.setItem(mockKey(bannerId), JSON.stringify({ ...body, lastUpdated: new Date().toISOString() }));
      return body;
    }
    const response = await apiClient.put(`/Banner/update/${bannerId}`, body);
    return response.data;
  }
}
export default new BannerService()
